
import React, { useState } from 'react';
import './CrmPage.css';
import PageHeader from '../../components/shared/PageHeader';
import Card from '../../components/shared/Card';
import Button from '../../components/shared/Button';
import Badge from '../../components/shared/Badge';
import { useToast } from '../../context/ToastContext';

const CONTACTS = [
  { id: 1, name: 'Sarah Mitchell', company: 'Acme Corp', email: 'sarah@acme.com', status: 'Active', value: '$12,400', avatar: 'SM', phone: '+1 (415) 555-0192' },
  { id: 2, name: 'James Reeves', company: 'BlueSky Inc', email: 'james@bluesky.io', status: 'Lead', value: '$5,800', avatar: 'JR', phone: '+1 (628) 555-0145' },
  { id: 3, name: 'Elena Vasquez', company: 'Meridian Labs', email: 'elena@meridian.co', status: 'Active', value: '$28,100', avatar: 'EV', phone: '+1 (510) 555-0173' },
  { id: 4, name: 'Tom Nakamura', company: 'Orbit Software', email: 'tom@orbit.dev', status: 'Churned', value: '$3,200', avatar: 'TN', phone: '+1 (408) 555-0188' },
  { id: 5, name: 'Priya Sharma', company: 'Synapse AI', email: 'priya@synapse.ai', status: 'Active', value: '$41,900', avatar: 'PS', phone: '+1 (650) 555-0101' },
  { id: 6, name: 'Marcus Lee', company: 'Lighthouse Media', email: 'marcus@lh.com', status: 'Lead', value: '$7,600', avatar: 'ML', phone: '+1 (415) 555-0167' },
];

const STATUS_VARIANT: Record<string, any> = {
  Active: 'success', Lead: 'ocean', Churned: 'danger',
};

export default function CrmPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [selected, setSelected] = useState<typeof CONTACTS[0] | null>(null);
  const { addToast } = useToast();

  const filtered = CONTACTS.filter(c =>
    (filter === 'All' || c.status === filter) &&
    (c.name.toLowerCase().includes(search.toLowerCase()) || c.company.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="crm-page">
      <PageHeader
        title="Customers"
        breadcrumb={['Business', 'Customers']}
        actions={<Button icon="+" size="sm" onClick={() => addToast('info', 'Add customer form coming soon!')}>Add Customer</Button>}
      />
      <div className="crm-kpi-row">
        {[
          { label: 'Total Contacts', value: '1,284', icon: '👥' },
          { label: 'Active Clients', value: '847', icon: '✅' },
          { label: 'Open Leads', value: '231', icon: '🎯' },
          { label: 'Pipeline Value', value: '$284K', icon: '💰' },
        ].map(k => (
          <div key={k.label} className="crm-stat">
            <span className="crm-stat__icon">{k.icon}</span>
            <span className="crm-stat__val">{k.value}</span>
            <span className="crm-stat__label">{k.label}</span>
          </div>
        ))}
      </div>

      <div className="crm-layout">
        <Card noPad className="crm-table-card">
          <div className="crm-toolbar">
            <input className="crm-search" placeholder="Search contacts…" value={search} onChange={e => setSearch(e.target.value)} />
            <div className="crm-filters">
              {['All','Active','Lead','Churned'].map(f => (
                <button key={f} className={`crm-filter${filter===f?' crm-filter--active':''}`} onClick={() => setFilter(f)}>{f}</button>
              ))}
            </div>
          </div>
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead>
                <tr><th>Contact</th><th>Company</th><th>Status</th><th>Value</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} className={selected?.id === c.id ? 'crm-row--selected' : ''} onClick={() => setSelected(c)}>
                    <td>
                      <div className="crm-contact-cell">
                        <div className="crm-avatar">{c.avatar}</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--color-text)' }}>{c.name}</div>
                          <div style={{ fontSize: '11px', color: 'var(--color-muted)' }}>{c.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="crm-muted">{c.company}</td>
                    <td><Badge variant={STATUS_VARIANT[c.status]}>{c.status}</Badge></td>
                    <td className="crm-value">{c.value}</td>
                    <td>
                      <div className="crm-actions">
                        <button className="crm-act-btn" title="Edit" onClick={e => { e.stopPropagation(); addToast('info', `Editing ${c.name}`); }}>✏</button>
                        <button className="crm-act-btn crm-act-btn--danger" title="Delete" onClick={e => { e.stopPropagation(); addToast('info', `Deleted ${c.name}`); }}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {selected && (
          <div className="crm-drawer">
            <div className="crm-drawer__header">
              <div className="crm-drawer__avatar">{selected.avatar}</div>
              <div>
                <div className="crm-drawer__name">{selected.name}</div>
                <div className="crm-drawer__company">{selected.company}</div>
              </div>
              <button className="card-icon-btn" style={{ marginLeft: 'auto' }} onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="crm-drawer__body">
              <div className="crm-drawer__field"><span>Email</span><strong>{selected.email}</strong></div>
              <div className="crm-drawer__field"><span>Phone</span><strong>{selected.phone}</strong></div>
              <div className="crm-drawer__field"><span>Status</span><Badge variant={STATUS_VARIANT[selected.status]}>{selected.status}</Badge></div>
              <div className="crm-drawer__field"><span>Lifetime Value</span><strong style={{ color: 'var(--color-success)' }}>{selected.value}</strong></div>
              <div className="crm-drawer__section">Activity</div>
              {[
                { icon: '💳', text: 'Invoice paid — $1,200', time: '2d ago' },
                { icon: '✉', text: 'Email opened: Q1 Proposal', time: '5d ago' },
                { icon: '📅', text: 'Meeting: Product Demo', time: '1w ago' },
              ].map((a, i) => (
                <div key={i} className="crm-drawer__activity">
                  <span>{a.icon}</span>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text)' }}>{a.text}</div>
                    <div style={{ fontSize: '11px', color: 'var(--color-muted)' }}>{a.time}</div>
                  </div>
                </div>
              ))}
              <div className="crm-drawer__actions">
                <Button size="sm" icon="✉" onClick={() => addToast('info', 'Opening inbox…')}>Email</Button>
                <Button size="sm" variant="ghost" icon="📄" onClick={() => addToast('info', 'Creating invoice…')}>Invoice</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
